import { Box, Grid, IconButton, LinearProgress, Paper, Theme, Tooltip, Typography, createStyles, makeStyles } from '@material-ui/core';
import ConfirmDeleteModal, { IConfirmDeleteModal } from './Components/ConfirmDeleteModal';
import { DeleteIcon, EditIcon } from './../../src/Layout/Components/Icons';
import React, { ReactElement, useState } from 'react';
import { colors } from './../../src/Constants/constants';
import ArrowBackIosIcon from '@material-ui/icons/ArrowBackIos';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import history from '../../src/Utils/history';

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		root: {
			padding: theme.spacing(3, 2),
			maxWidth: 'calc(100vw - 300px)',
			[theme.breakpoints.down('sm')]: {
				maxWidth: '100%',
			},
		},
	})
);

export interface IEntityDetailsProps extends IEntityDetailsBaseProps {
	isEditable?: boolean;
	isRemovable?: boolean;

	headerButtons?: ReactElement;
	entityDetailsComponent: ReactElement;
	entityActionIconsComponent: ReactElement;
	entitySubsetTableComponent: ReactElement;

	placeEntityActionNearTitle?: boolean;
}
export interface IEntityDetailsBaseProps {
	entity: any;
	api: any;
	openModal: any;
	detailTitle: string;
	entityType: string;
	historyBackUrl?: string;
	disableHistoryBackUrl?: boolean;
}

export default function EntityDetails(props: IEntityDetailsProps) {
	const classes = useStyles();
	const { t } = useTranslation();
	const {
		api,
		openModal,
		isEditable,
		isRemovable,
		entity,
		entityDetailsComponent,
		entityActionIconsComponent,
		entitySubsetTableComponent,
		headerButtons,
	} = props;
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
	const { enqueueSnackbar } = useSnackbar();
	const savedDialog = (isNew = false) =>
		enqueueSnackbar(`${isNew ? t('common.dataSaved') : t('common.changesSaved')}`, { variant: 'success' });
	const errorDialog = (message?: string) =>
		enqueueSnackbar(`${t('common.errorOccured')}${message ? ': ' : ''}${message && message}`, { variant: 'error' });
	const handleDelete = () => {
		entity.id = entity.id ?? entity.deviceId ?? entity.areaId ?? entity.locationId ?? entity.corporationId ?? entity.dealerId;
		onRowDelete(entity.id);
	};

	const onRowDelete = async function (id: string) {
		try {
			const response = await api.Delete(id);
			if (response.success) {
				savedDialog();
				props.historyBackUrl ? history.push(props.historyBackUrl) : window.history.back();
			} else {
				let message = t(`errorCodes.${response.errors[0].errorCode}`);
				enqueueSnackbar(`${t('common.errorOccured')}: ${message}`, { variant: 'error' });
			}
		} catch (e) {
			errorDialog(e.message);
		}
	};

	const deleteModalProps: IConfirmDeleteModal = {
		handleDelete: handleDelete,
		isOpen: isDeleteModalOpen,
		handleClose: () => setIsDeleteModalOpen(false),
	};

	return entity ? (
		<>
			{isDeleteModalOpen && <ConfirmDeleteModal {...deleteModalProps} />}
			<Paper className={classes.root}>
				<>
					<div style={{ display: 'flex' }}>
						<div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', marginLeft: '20px' }}>
							{props.disableHistoryBackUrl ? (
								<Box display='flex' flexDirection='row' p={1} m={1}></Box>
							) : (
								<Tooltip
									title={t('common.back') || ''}
									onClick={() => (props.historyBackUrl ? history.push(props.historyBackUrl) : window.history.back())}
								>
									<div style={{ color: colors.main }}>
										<IconButton color='inherit'>
											<ArrowBackIosIcon viewBox=' 0 0 13 24' />
										</IconButton>
									</div>
								</Tooltip>
							)}

							<Box>
								<Typography style={{ minWidth: '100px' }} variant='h6'>
									{props.detailTitle}
								</Typography>
								<Grid className='info-box-content-item-label'>{props.entityType}</Grid>
							</Box>
							{headerButtons}
						</div>
						<div style={{ flex: 'auto' }}></div>
						<div style={{ marginRight: '20px' }}>
							{props.placeEntityActionNearTitle && entityActionIconsComponent}
							{isEditable && (
								<Tooltip title={t('table.body.editTooltip') || ''}>
									<IconButton onClick={() => (openModal ? openModal(true) : null)}>
										<EditIcon isSelected={true} />
									</IconButton>
								</Tooltip>
							)}
							{isRemovable && (
								<Tooltip title={t('table.body.deleteTooltip') || ''}>
									<IconButton onClick={() => setIsDeleteModalOpen(true)}>
										<DeleteIcon isSelected={true} />
									</IconButton>
								</Tooltip>
							)}
						</div>
					</div>
					<Grid container>
						<Grid item xs={12} sm={7} style={{ overflowWrap: 'anywhere' }}>
							{entityDetailsComponent}
						</Grid>
						<Grid container item xs={12} sm={5} alignContent='space-around' alignItems='center' justify='space-evenly'>
							{!props.placeEntityActionNearTitle && entityActionIconsComponent}
						</Grid>
						<Grid item xs={12}>
							<Box mt={4}></Box>
						</Grid>
					</Grid>
					<div className='proportional' style={{margin: '0 30px'}}>{entitySubsetTableComponent}</div>
				</>
			</Paper>
		</>
	) : (
		<LinearProgress style={{ marginTop: '8px', zIndex: 1301 }} />
	);
}
